import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';

import { isLocalUsingMeridianNotation } from '../../utils/meridian.helper';
import { DateRange } from './calendar/DateRange';
import { ShortcutRange } from './shortcut.range';
import { Time } from './time';

type TimeInput = 'start' | 'end';

@Component({
  selector: 'ls-datepicker',
  templateUrl: './datepicker.component.html'
})
export class DatepickerComponent implements OnInit, OnChanges, OnDestroy {
  // tslint:disable-next-line:no-input-rename
  @Input('range')
  public providedDateRange: DateRange;
  @Input()
  public ranges: Array<ShortcutRange>;
  @Input()
  public position: 'left' | 'center' | 'right' = 'center';
  @Output()
  public dateChanged: EventEmitter<DateRange> = new EventEmitter();

  public minutes: Array<number> = [0, 15, 30, 45];
  public showCalendar = false;
  public defaultRanges: Array<ShortcutRange> = [
    {
      label: 'Today',
      range: new DateRange(
        moment().startOf('day'),
        moment()
          .startOf('day')
          .add(1, 'day')
      )
    },
    {
      label: 'Yesterday',
      range: new DateRange(
        moment()
          .startOf('day')
          .add(-1, 'day'),
        moment().startOf('day')
      )
    },
    {
      label: 'This Week',
      range: new DateRange(
        moment().startOf('week'),
        moment()
          .startOf('week')
          .add(1, 'week')
      )
    },
    {
      label: 'Last Week',
      range: new DateRange(
        moment()
          .startOf('week')
          .add(-1, 'week'),
        moment().startOf('week')
      )
    },
    {
      label: 'This Month',
      range: new DateRange(
        moment().startOf('month'),
        moment()
          .startOf('month')
          .add(1, 'month')
      )
    },
    {
      label: 'Last Month',
      range: new DateRange(
        moment()
          .startOf('month')
          .add(-1, 'month'),
        moment().startOf('month')
      )
    }
  ];
  public hours: Array<number> = [];
  public currentMonthStart: number;
  public currentMonthEnd: number;
  public alignLeft = false;
  public alignRight = false;
  private selectedDateRange: DateRange;
  private startTime: Time;
  private endTime: Time;
  private unregisterOutsideClick: Function;

  constructor(private renderer2: Renderer2, private elementRef: ElementRef) {
    if (isLocalUsingMeridianNotation()) {
      this.hours = _.range(1, 13);
    } else {
      this.hours = _.range(0, 24);
    }
  }

  public ngOnInit(): void {
    this.ranges = this.ranges || this.defaultRanges;
    this.setSelectedDateRange();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['providedDateRange'] &&
      !this.isSameDateRange(
        changes['providedDateRange'].currentValue,
        changes['providedDateRange'].previousValue
      )
    ) {
      this.setSelectedDateRange();
    }
  }

  public ngOnDestroy(): void {
    if (this.unregisterOutsideClick) {
      this.unregisterOutsideClick();
    }
  }

  public calendarDateSelected(date: moment.Moment): void {
    if (
      this.selectedDateRange.end != null ||
      this.selectedDateRange.start.isAfter(date, 'day')
    ) {
      this.selectedDateRange.end = null;
      this.selectedDateRange.start = date;
      this.currentMonthStart = date.month();
    } else {
      this.selectedDateRange.end = date;
      this.currentMonthStart = this.selectedDateRange.start.month();
      this.currentMonthEnd = date.month();
      this.selectedDateRange = _.clone(this.selectedDateRange);
    }
  }

  public meridianNotation(): boolean {
    return isLocalUsingMeridianNotation();
  }

  public updateTime(timeInput: TimeInput): void {
    if (this.selectedDateRange[timeInput]) {
      const time: Time = timeInput === 'start' ? this.startTime : this.endTime;
      this.selectedDateRange[timeInput] = this.selectedDateRange[timeInput]
        .clone()
        .hour(this.get24Hour(time.hour, time.meridian === 'AM'))
        .minute(time.minute);
    }
  }

  public formatDateRange(range: DateRange): string {
    let format: string = range.start.format('L LT');
    if (range.end) {
      format += ' - ' + range.end.format('L LT');
    }

    return format;
  }

  public toggleCalendar(
    event?: MouseEvent,
    cancelAction: boolean = false
  ): void {
    this.showCalendar = !this.showCalendar;
    if (this.showCalendar) {
      this.calculateOffset();
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.currentMonthStart = this.selectedDateRange.start.month();
      this.currentMonthEnd = this.selectedDateRange.end.month();
      this.unregisterOutsideClick = this.renderer2.listen(
        'document',
        'click',
        this.clickHandeler.bind(this)
      );
    } else if (this.unregisterOutsideClick) {
      this.unregisterOutsideClick();
      if (cancelAction) {
        this.selectedDateRange = this.providedDateRange.clone();
      }
      this.updateDateRange();
    }
  }

  public applyShortcutRange(range: DateRange): void {
    this.selectedDateRange = range.clone();
    this.toggleCalendar();
  }

  private setSelectedDateRange(): void {
    if (!this.providedDateRange) {
      this.selectedDateRange = new DateRange(
        moment().startOf('day'),
        moment().endOf('day')
      );
    } else {
      this.selectedDateRange = this.providedDateRange.clone();
    }

    this.startTime = {
      hour: this.getLocalHour(this.selectedDateRange.start.hour()),
      minute: this.selectedDateRange.start.minute()
    };

    this.endTime = {
      hour: this.getLocalHour(this.selectedDateRange.end.hour()),
      minute: this.selectedDateRange.end.minute()
    };

    if (this.meridianNotation()) {
      this.startTime.meridian = this.selectedDateRange.start.format('A');
      this.endTime.meridian = this.selectedDateRange.end.format('A');
    }
  }

  private getLocalHour(hour: number): number {
    if (this.meridianNotation()) {
      return ((hour + 11) % 12) + 1;
    } else {
      return hour;
    }
  }

  private get24Hour(hour: number, isAm: boolean): number {
    if (this.meridianNotation()) {
      if (isAm) {
        return hour % 12;
      } else {
        return (hour % 12) + 12;
      }
    }

    return hour;
  }

  private clickHandeler(event: MouseEvent): void {
    const clickedInside: boolean = this.elementRef.nativeElement.contains(
      event.target
    );
    if (!clickedInside) {
      this.toggleCalendar(event, this.selectedDateRange.end == null);
    }
  }

  private updateDateRange(): void {
    if (
      this.selectedDateRange.start &&
      this.selectedDateRange.end &&
      !this.isSameDateRange(this.providedDateRange, this.selectedDateRange)
    ) {
      this.providedDateRange = this.selectedDateRange.clone();
      this.dateChanged.emit(this.providedDateRange);
    } else {
      this.selectedDateRange = this.providedDateRange.clone();
    }
  }

  private isSameDateRange(range1: DateRange, range2: DateRange): boolean {
    if (!range1 || !range2) {
      return false;
    }
    const startIsSame: boolean =
      range1.start &&
      range2.start &&
      range1.start.unix() === range2.start.unix();
    const endIsSame: boolean =
      range1.end && range2.end && range1.end.unix() === range2.end.unix();
    return startIsSame && endIsSame;
  }

  private calculateOffset(): void {
    const margin = 50;
    const elem: any = this.elementRef.nativeElement;
    const left: boolean = elem.offsetLeft < margin;
    const right: boolean =
      elem.parentElement.clientWidth - (elem.offsetLeft + elem.clientWidth) <
      margin;
    this.alignLeft = left && !right;
    this.alignRight = !left && right;
  }
}
